import { useEffect, useState } from "react";
import { Crown, Check, Coins, Gift, Copy, Users, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

export default function Premium() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [txs, setTxs] = useState([]);
  const [referral, setReferral] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  const load = async () => {
    const [p, t, r] = await Promise.all([api.get("/billing/plans"), api.get("/billing/transactions"), api.get("/referral/me")]);
    setPlans(p.data); setTxs(t.data); setReferral(r.data);
  };
  useEffect(() => { load(); }, []);

  const checkout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  const cancel = async () => {
    try { await api.post("/billing/cancel"); await refresh(); toast.success("Abbonamento annullato"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copyLink = () => { navigator.clipboard.writeText(referral.link); toast.success("Link invito copiato!"); };
  const [redeemCode, setRedeemCode] = useState("");
  const redeem = async () => {
    if (!redeemCode.trim()) return;
    try { await api.post("/billing/redeem-code", { code: redeemCode }); await refresh(); load(); setRedeemCode(""); toast.success("Crediti riscattati!"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const shareLink = () => {
    const text = `Unisciti a Ti Regalo e ricevi crediti di benvenuto! ${referral.link}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referral.link)}&quote=${encodeURIComponent(text)}`, "_blank");
  };

  if (!plans) return null;
  const isPremium = user?.plan === "premium";
  const eur = (n) => Number(n).toFixed(2).replace(".", ",");

  return (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-4">
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight">Premium & Crediti</h1>
        <p className="text-muted-foreground mt-2">Guadagna crediti donando e invitando, o sblocca vantaggi con Premium.</p>
      </div>

      <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gold))]/15 border border-[hsl(var(--gold))]/40 px-5 py-2.5 mx-auto mb-8 w-full justify-center" data-testid="credit-balance">
        <Coins className="h-5 w-5 text-[hsl(var(--gold))]" />
        <span className="font-head font-extrabold text-xl">{user?.credits} crediti</span>
        <span className="text-muted-foreground text-sm">disponibili</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border/60 bg-card p-8 tactile-shadow">
          <h3 className="font-head font-bold text-xl">{plans.free.name}</h3>
          <div className="font-head font-extrabold text-4xl mt-2">€0<span className="text-base font-normal text-muted-foreground">/mese</span></div>
          <ul className="mt-6 space-y-3 text-sm">
            {plans.free.benefits.map((b) => <li key={b} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {b}</li>)}
          </ul>
          {!isPremium && <Badge variant="outline" className="mt-6">Piano attuale</Badge>}
        </div>

        <div className="rounded-2xl border-2 border-[hsl(var(--gold))]/60 bg-card p-8 tactile-shadow relative overflow-hidden">
          <div className="absolute top-4 right-4"><Crown className="h-6 w-6 text-[hsl(var(--gold))]" /></div>
          <h3 className="font-head font-bold text-xl">{plans.premium.name}</h3>
          <div className="font-head font-extrabold text-4xl mt-2">€{eur(plans.premium.price)}<span className="text-base font-normal text-muted-foreground">/mese</span></div>
          <ul className="mt-6 space-y-3 text-sm">
            {plans.premium.benefits.map((b) => <li key={b} className="flex gap-2"><Check className="h-4 w-4 text-[hsl(var(--gold))] shrink-0 mt-0.5" /> {b}</li>)}
          </ul>
          {isPremium ? (
            <Button variant="outline" className="mt-6 w-full rounded-full" onClick={cancel} data-testid="cancel-premium-btn">Annulla abbonamento</Button>
          ) : (
            <Button className="mt-6 w-full rounded-full bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-white" disabled={loadingKey === plans.premium.lookup_key} onClick={() => checkout(plans.premium.lookup_key)} data-testid="subscribe-btn">
              {loadingKey === plans.premium.lookup_key ? "Reindirizzo..." : "Passa a Premium"}
            </Button>
          )}
        </div>
      </div>

      <h2 className="font-head font-bold text-2xl mt-12 mb-4">Acquista crediti</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Object.entries(plans.credit_packs).map(([key, pack]) => (
          <div key={key} className="rounded-2xl border border-border/60 bg-card p-6 text-center tactile-shadow">
            <Coins className="h-8 w-8 text-[hsl(var(--gold))] mx-auto" />
            <div className="font-head font-extrabold text-3xl mt-3">{pack.credits}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">crediti</div>
            <Button className="mt-4 w-full rounded-full" variant="outline" disabled={loadingKey === pack.lookup_key} onClick={() => checkout(pack.lookup_key)} data-testid={`buy-${key}`}>
              {loadingKey === pack.lookup_key ? "..." : `€${eur(pack.price)}`}
            </Button>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-card border border-border/60 p-6 mt-6 tactile-shadow" data-testid="redeem-card">
        <h2 className="font-head font-bold text-xl">Hai un codice crediti?</h2>
        <p className="text-sm text-muted-foreground mt-1">Riscatta un codice prepagato per aggiungere crediti al tuo account.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="RG-XXXXXXXX" className="rounded-full h-11" data-testid="redeem-input" />
          <Button onClick={redeem} className="rounded-full h-11 px-6" data-testid="redeem-btn">Riscatta</Button>
        </div>
      </div>

      {referral && (
        <div className="rounded-2xl bg-primary text-primary-foreground p-6 sm:p-8 mt-10 tactile-shadow" data-testid="referral-card">
          <div className="flex items-center gap-2"><Users className="h-5 w-5" /><h2 className="font-head font-bold text-xl">Invita e guadagna</h2></div>
          <p className="text-primary-foreground/80 mt-2 text-sm max-w-lg">
            Condividi il tuo link (perfetto per il gruppo Facebook!). Ricevi <b>{referral.reward} crediti</b> per ogni iscritto e loro <b>{referral.referee_bonus} crediti</b> bonus.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Input readOnly value={referral.link} className="bg-white/10 border-white/20 text-white rounded-full h-11" data-testid="referral-link" />
            <div className="flex gap-2">
              <Button onClick={copyLink} variant="secondary" className="rounded-full h-11" data-testid="copy-referral"><Copy className="h-4 w-4 mr-1" /> Copia</Button>
              <Button onClick={shareLink} variant="secondary" className="rounded-full h-11" data-testid="share-referral"><Share2 className="h-4 w-4 mr-1" /> Condividi</Button>
            </div>
          </div>
          <div className="mt-4 text-sm text-primary-foreground/80">Inviti a segno: <b className="text-white">{referral.referrals_count}</b></div>
        </div>
      )}

      <div className="rounded-2xl bg-primary/5 border border-primary/20 p-6 mt-8 flex items-start gap-3">
        <Gift className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Guadagna crediti gratis:</span> ricevi <b>{plans.config.donation_reward} crediti</b> per ogni oggetto che doni e consegni. Ogni richiesta costa {plans.config.request_cost} credito oltre il limite gratuito mensile.
        </div>
      </div>

      {txs.length > 0 && (
        <>
          <h2 className="font-head font-bold text-2xl mt-12 mb-4">Movimenti crediti</h2>
          <div className="rounded-2xl border border-border/60 divide-y divide-border/60 overflow-hidden">
            {txs.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`tx-${t.id}`}>
                <span>{t.reason}</span>
                <span className={`font-semibold ${t.amount >= 0 ? "text-success" : "text-destructive"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
